import { AutofillMonitor } from '@angular/cdk/text-field';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentUser } from '@core/services/auth/auth.model';
import { AuthService } from '@core/services/auth/auth.service';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { NgIdleService } from '@core/services/user-idle-manager/ng-idle.service';
import { TermsAndConditionsDialogComponent } from 'app/features/auth/terms-and-conditions/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { PasswordExpireDialogComponent } from 'app/features/user/components/password-expire-dialog/password-expire-dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppConfig } from '../../../app.config';

@Component({
  selector: 'Teranet-Login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('password', { read: ElementRef }) refPassword: ElementRef<HTMLElement>;
  @ViewChild('userName', { read: ElementRef }) refUserName: ElementRef<HTMLElement>;
  errorMessage: string;
  isAccountLocked: boolean = false;
  isLoginError: boolean = false;
  passwordAutofilled: boolean;
  private user = new CurrentUser();
  reactiveForm: FormGroup;
  userNameAutofilled: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private autofill: AutofillMonitor,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private idle: NgIdleService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private spinner: NgxSpinnerService
  ) {
    if (this.authService.isAuthenticated()) {
      this.router.navigateByUrl('dashboard');
    }
    this.reactiveForm = this.fb.group({
      email: new FormControl('', Validators.required),
      password: new FormControl('', Validators.required),
    });
    if (this.authService.currentUserValue) {
      this.reactiveForm.get('email').setValue(this.authService.currentUserValue.email);
      this.reactiveForm.get('password').setValue('');
    }
  }

  ngOnInit() {
    this.spinner.show();
    this.activatedRoute.queryParams.subscribe(params => {
      this.isAccountLocked = params['isAccountLocked'];
      this.errorMessage = 'Your account has been locked. Please contact your Administrator or contact Teranet at customer.care@teranet.ca';
    });
    this.spinner.hide();
  }

  login() {
    this.authService.logout();
    this.localStorageService.removeItem('currentUser');
    this.spinner.show();
    this.isLoginError = false;
    this.isAccountLocked = false;
    setTimeout(() => {
      /** spinner ends after 0.5 seconds */
      this.authService.login(this.reactiveForm.get('email').value, this.reactiveForm.get('password').value).subscribe(
        (data: any) => {
          if (data && data.isUnAuthorizedIP) {
            this.router.navigate(['error'], {
              state: {
                icon: 'exclamation',
                color: 'red',
                title: 'Unauthorized Access',
              },
            });
            this.spinner.hide();
          }
          // login successful if there's a jwt token in the response
          else if (data && data.accesstoken) {
            this.user.username = data.username;
            this.user.email = this.reactiveForm.get('email').value;
            this.user.authenticated = !data.isChangePasswordForced && data.isPasswordSecurityDone;
            this.user.corporationid = data.corporationid;
            this.user.role = data.role;
            this.user.pages = data.pages;
          // store user details and jwt token in local storage to keep user logged in between page refreshes
            this.localStorageService.setItem('currentUser', this.user);
            this.authService.currentUserSubject.next(this.user);
            this.localStorageService.setItem('accesstoken', data.accesstoken);
            this.localStorageService.setItem('refreshtoken', data.refreshtoken);
            this.localStorageService.setItem('apiEndpoint', AppConfig.settings.settings.apiEndpoint);
            this.localStorageService.setItem('browserName', this.authService.getBrowserNameAndVersion().name);
            this.localStorageService.setItem('browserVersion', this.authService.getBrowserNameAndVersion().version);
            this.idle.initialIdleService();
            this.authService.startRefreshTokenTimer();
            if (
              data.isTermAccepted &&
              !data.isChangePasswordForced && data.isPasswordSecurityDone &&
              data.passwordExpireDays === 0
            ) {
              this.router.navigateByUrl('/dashboard');
            } else if (!data.isTermAccepted) {
              this.openTermAcceptanceDialog(data.isPasswordSecurityDone, data.isChangePasswordForced);
            } else if (!data.isPasswordSecurityDone) {
              this.router.navigateByUrl('/passwordsecurity?isChangePasswordForced=' + data.isChangePasswordForced);
            } else if (data.isChangePasswordForced) {
              this.router.navigateByUrl('/updatepassword');
            } else if (data.passwordExpireDays > 0) {
              this.openPasswordExpireDialog(data.passwordExpireDays);
            } else {
              this.authService.logout();
            }
            //this.router.navigateByUrl('dashboard');
            this.spinner.hide();
          }
        },
        (err: HttpErrorResponse) => {
          this.spinner.hide();
          this.isLoginError = true;
          this.reactiveForm.get('password').setValue('');
          if (err.status == 404) {
            this.errorMessage = err.error + " User doesn't exist.";
          } else if (err.status == 0) {
            this.errorMessage = 'API call failed.';
          } else {
            this.errorMessage = err.error;
          }
        }
      );
    }, 500);
  }

  openTermAcceptanceDialog(isPasswordSecurityDone, isChangePasswordForced) {
    const dialogRef = this.dialog.open(TermsAndConditionsDialogComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.authService.setTermandConditionAcceptanceDate().subscribe(
          (data: any) => {
            if (!isPasswordSecurityDone) {
              this.router.navigateByUrl('/passwordsecurity?isChangePasswordForced=' + isChangePasswordForced);
            }
            else if (isChangePasswordForced) {
              this.router.navigateByUrl('/updatepassword');
            } else {
              this.router.navigateByUrl('/dashboard');
            }
          },
          (err: HttpErrorResponse) => {
            this.spinner.hide();
            this.isLoginError = true;
            if (err.status == 404) {
              this.errorMessage = 'Fatal Error.';
            } else {
              this.errorMessage = err.error;
            }
          }
        );
      } else {
        this.authService.logout();
      }
    });
  }

  openPasswordExpireDialog(days) {
    const dialogRef = this.dialog.open(PasswordExpireDialogComponent, {
      data: days,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.router.navigateByUrl('/updatepassword');
      } else {
        this.router.navigateByUrl('/dashboard');
      }
    });
  }

  valueChange(event: any) {
    this.isLoginError = false;
  }

  ngAfterViewInit() {
    this.autofill.monitor(this.refUserName)
      .subscribe(e => this.userNameAutofilled = e.isAutofilled);
    this.autofill.monitor(this.refPassword)
      .subscribe(e => this.passwordAutofilled = e.isAutofilled);
  }

  ngOnDestroy() {
    this.autofill.stopMonitoring(this.refUserName);
    this.autofill.stopMonitoring(this.refPassword);
  }
}
